
import React, { useState, useEffect, useRef } from 'react';
import storage from 'src/utils/storageUtils';
import {
    AddContainer, Component4, Texts, InputTextComponent,
    PrescriptionViewBox, PresText, PresTitle, EditButton, SaveButton, LabelWithTooltip
} from './Style';
import InputSelect from 'src/components/shared/Form/Select';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogTitle, DialogContent, Typography, Chip, Stack } from '@mui/material';
import { getTemplateByClinicIdRequest, getClinicByIdRequest, getDoctorProfileByIdRequest, getPatientRequest } from 'src/modules/app/store/appActions';
import { ReactComponent as Edit } from '../../../../../assets/images/edit-2.svg';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg';
import { ReactComponent as Close } from '../../../../../assets/images/close-circle.svg';
import theme from 'src/config/theme';
import useResponsive from 'src/components/hooks/useResponsive';
import { getUserFromToken } from 'src/DecodeToken';
import dayjs from 'dayjs';

const Language = [
    { label: 'English', value: 'english' },
    { label: 'Hindi', value: 'hindi' },
    { label: 'Gujarati', value: 'gujarati' },
];

const VARIABLE_GROUPS = [
    {
        label: 'Insert Variable',
        variables: [
            { label: 'Patient Name', value: '{{patient_name}}' },
            { label: 'Doctor Name', value: '{{doctor_name}}' },
            { label: 'Appointment Date', value: '{{appointment_date}}' },
            { label: 'Appointment Time', value: '{{appointment_time}}' },
            { label: 'Clinic Name', value: '{{clinic_name}}' },
            { label: 'Clinic Address', value: '{{clinic_address}}' },
            { label: 'Clinic Phone', value: '{{clinic_phone}}' },
            { label: 'Mobile Number', value: '{{mobile_number}}' },
        ]
    }
];

const MessageTemplate = ({ innerRef, data, setClinicProfileData, clinicId }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    
    // Helper to synchronously format templates for instant rendering
    const formatMessageTemplates = (rawTemplates) => {
        if (!Array.isArray(rawTemplates)) return [];
        return rawTemplates.map((template, idx) => ({
            id: template.id || idx,
            label: template.title || template.label,
            value: template.title || template.label,
            text: template.text,
            language: template.language || 'english',
        }));
    };

    const [localTemplates, setLocalTemplates] = useState(() => formatMessageTemplates(data));
    const [openDialog, setOpenDialog] = useState(false);
    const [previewData, setPreviewData] = useState({
        clinic: null,
        doctor: null,
        patient: null
    });
    const textRef = useRef(null);
    const dispatch = useDispatch();
    const { isMobile, isTablet } = useResponsive();
    const Width = isMobile || isTablet ? '18px' : '20px';
    const Height = isMobile || isTablet ? '18px' : '20px';

    useEffect(() => {
        if (openDialog && clinicId && !previewData.clinic) {
            // Fetch clinic details for preview only when dialog is open
            dispatch(getClinicByIdRequest(clinicId, (res) => {
                setPreviewData(prev => ({ ...prev, clinic: res?.data || res }));
            }));
        }
    }, [openDialog, clinicId, dispatch, previewData.clinic]);

    useEffect(() => {
        const userId = getUserFromToken();
        if (openDialog && userId && !previewData.doctor) {
            // Fetch doctor details for preview only when dialog is open
            dispatch(getDoctorProfileByIdRequest(userId, (res) => {
                setPreviewData(prev => ({ ...prev, doctor: res?.data || res }));
            }));
        }
    }, [openDialog, dispatch, previewData.doctor]);

    useEffect(() => {
        if (openDialog && !previewData.patient) {
            // Fetch last registered patient for preview only when dialog is open
            dispatch(getPatientRequest({ params: { limit: 1, sort: 'createdAt:desc' } }, (res) => {
                if (res && res.data && res.data.length > 0) {
                    setPreviewData(prev => ({ ...prev, patient: res.data[0] }));
                }
            }));
        }
    }, [openDialog, dispatch, previewData.patient]);

    useEffect(() => {
        setLocalTemplates(formatMessageTemplates(data));
    }, [JSON.stringify(data)]);

    const handleEdit = (template) => {
        setSelectedTemplate(template);
        setOpenDialog(true);
    };

    const handleDelete = (templateToDelete) => {
        const updatedTemplates = localTemplates.filter(t => t.id !== templateToDelete.id);
        setLocalTemplates(updatedTemplates);
        setClinicProfileData(prevData => ({
            ...prevData,
            messageTemplate: updatedTemplates.map(t => ({
                title: t.label,
                text: t.text,
                type: 'message',
                language: t.language,
            })),
        }));
    };
    const handleAdd = () => {
        setSelectedTemplate(null);
        setOpenDialog(true);
    };
    const handleSubmit = (values) => {
        if (!values.title.trim() || !values.text.trim()) {
            return;
        }
        setLocalTemplates(prev => {
            const isExisting = selectedTemplate
                ? prev.find(t => t.id === selectedTemplate.id)
                : false;

            let updatedTemplates;
            if (isExisting) {
                updatedTemplates = prev.map(t =>
                    t.id === selectedTemplate.id ? {
                        ...t,
                        label: values.title,
                        value: values.title,
                        text: values.text,
                        language: values.language,
                    } : t
                );
            } else {
                const duplicate = prev.find(t =>
                    t.label.trim() === values.title.trim() &&
                    t.text.trim() === values.text.trim() &&
                    t.language === values.language
                );

                if (duplicate) return prev;
                updatedTemplates = [
                    ...prev,
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        label: values.title,
                        value: values.title,
                        text: values.text,
                        language: values.language,
                    }
                ];
            }
            const uniqueTemplates = updatedTemplates.filter((t, index, self) =>
                index === self.findIndex(
                    s => s.label.trim() === t.label.trim() && s.text.trim() === t.text.trim() && s.language === t.language
                )
            );
            setClinicProfileData(prevData => ({
                ...prevData,
                messageTemplate: uniqueTemplates.map(t => ({
                    title: t.label,
                    text: t.text,
                    type: 'message',
                    language: t.language,
                })),
            }));

            return uniqueTemplates;
        });
        setSelectedTemplate(null);
        setOpenDialog(false);
    };
    return (
        <>
            <AddContainer>
                {localTemplates.length > 0 && localTemplates.map((template) => (
                    <PrescriptionViewBox
                        key={template.id}
                    >
                        <Box>
                            <PresTitle>{template.label}</PresTitle>
                            <PresText>{template.text}</PresText>
                            <Typography variant="caption" color="textSecondary">Language: {template.language}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px' }}>
                            <EditButton type="button" onClick={() => handleEdit(template)}>
                                <Edit width={Width} />
                            </EditButton>
                            <EditButton type="button" onClick={() => handleDelete(template)}>
                                <Close width={Width} />
                            </EditButton>
                        </Box>
                    </PrescriptionViewBox>
                ))}

                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <SaveButton
                        variant="outlined"
                        startIcon={<Add width={Width} height={Height} sx={{ color: theme.palette.primary.main }} />}
                        onClick={handleAdd}
                    >
                        Add
                    </SaveButton>
                </Box>
            </AddContainer>

            {openDialog && (
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md"  >
                    <DialogTitle>{selectedTemplate ? 'Edit Template' : 'Add New Template'}</DialogTitle>
                    <DialogContent sx={{ p: { xs: 2, md: 4 }, minHeight: '500px' }}>
                        <Formik
                            initialValues={{
                                type: 'message',
                                language: selectedTemplate?.language || Language[0].value,
                                title: selectedTemplate?.label || '',
                                text: selectedTemplate?.text || '',
                            }}
                            enableReinitialize
                            onSubmit={handleSubmit}
                            innerRef={innerRef}
                        >
                            {({ values, handleChange, handleBlur, setFieldValue }) => {
                                
                                // Generate the parsed text once for preview.
                                // Fallbacks applied accurately
                                const RealDoctorName = storage.get('DOCTORNAME') || previewData.doctor?.profile?.fullName || previewData.doctor?.fullName || 'Dr. Mehta';

                                const parsedPreviewText = values.text
                                    .replace(/\{\{clinic_name\}\}/g, previewData.clinic?.clinicName || 'MyClinique Dental Clinic')
                                    .replace(/\{\{clinic_address\}\}/g, previewData.clinic?.address || '123 Medical Square, City Center')
                                    .replace(/\{\{clinic_phone\}\}/g, previewData.clinic?.phoneNo || '+91 9876543210')
                                    .replace(/\{\{patient_name\}\}/g, previewData.patient?.fullName || 'Harsh Patel')
                                    .replace(/\{\{doctor_name\}\}/g, RealDoctorName)
                                    .replace(/\{\{appointment_date\}\}/g, dayjs().add(1, 'day').format('D MMMM'))
                                    .replace(/\{\{appointment_time\}\}/g, '5:30 PM')
                                    .replace(/\{\{mobile_number\}\}/g, previewData.patient?.contactNo || '+91 9876543210');

                                const DEFAULT_TEMPLATES_i18n = {
                                    english: [
                                        { label: 'Blank/Custom Template', value: '', text: '' },
                                        { label: 'Appointment Scheduled', value: 'Appointment Scheduled', text: 'Hello {{patient_name}}, your appointment with {{doctor_name}} is confirmed for {{appointment_date}} at {{appointment_time}} at {{clinic_name}}. For queries, contact {{mobile_number}}.' },
                                        { label: 'Appointment Reminder', value: 'Appointment Reminder', text: 'Reminder: Hi {{patient_name}}, this is to remind you about your scheduled appointment with {{doctor_name}} on {{appointment_date}} at {{appointment_time}}. See you soon!' },
                                        { label: 'Welcome to Clinic', value: 'Welcome to Clinic', text: 'Welcome to {{clinic_name}}, {{patient_name}}! We are glad to have you. Please feel free to reach out to us at {{mobile_number}} for any assistance.' },
                                        { label: 'Post-Consultation / Thank You', value: 'Thank You for Visiting', text: 'Dear {{patient_name}}, thank you for visiting {{clinic_name}}. We hope you had a good experience with {{doctor_name}}. Please take care of your health!' },
                                        { label: 'Follow-up Reminder', value: 'Follow-up Reminder', text: 'Hello {{patient_name}}, this is {{clinic_name}}. It has been a while since your last visit. Please let us know if you need to schedule a follow-up with {{doctor_name}}.' },
                                        { label: 'Reschedule Notice', value: 'Appointment Rescheduled', text: 'Hi {{patient_name}}, your appointment at {{clinic_name}} has been rescheduled to {{appointment_date}} at {{appointment_time}} with {{doctor_name}}. Apologies for any inconvenience.' },
                                        { label: 'Prescription Ready', value: 'Prescription Ready', text: 'Hello {{patient_name}}, your prescription from {{doctor_name}} at {{clinic_name}} is ready. You can view it during your next visit or via our patient portal.' },
                                        { label: 'Lab Report Ready', value: 'Lab Report Ready', text: 'Hi {{patient_name}}, your lab report is now available at {{clinic_name}}. Please contact us at {{mobile_number}} for further details.' },
                                        { label: 'Payment Confirmation', value: 'Payment Confirmation', text: 'Dear {{patient_name}}, we have received your payment at {{clinic_name}}. Thank you for choosing us for your healthcare needs!' },
                                        { label: 'Feedback Request', value: 'Feedback Request', text: 'Hi {{patient_name}}, thank you for visiting {{clinic_name}}. We would love to hear about your experience with {{doctor_name}}. Please share your feedback with us.' },
                                        { label: 'Birthday Wishes', value: 'Birthday Wishes', text: 'Happy Birthday {{patient_name}}! Wishing you a healthy and happy year ahead. Warm regards, {{clinic_name}}.' },
                                        { label: 'Medicine Reminder', value: 'Medicine Reminder', text: 'Hi {{patient_name}}, this is a reminder from {{clinic_name}} to take your scheduled medicine. Stay healthy!' },
                                        { label: 'Holiday Notice', value: 'Holiday Notice', text: 'Dear {{patient_name}}, please note that {{clinic_name}} will be closed on [Date] for [Holiday]. For emergencies, contact {{mobile_number}}.' },
                                        { label: 'Treatment Scheduled', value: 'Treatment Scheduled', text: 'Hi {{patient_name}}, your treatment session for [Treatment Name] has been scheduled at {{clinic_name}} for {{appointment_date}} at {{appointment_time}}. See you then!' }
                                    ],
                                    hindi: [
                                        { label: 'Blank/Custom Template', value: '', text: '' },
                                        { label: 'Appointment Scheduled', value: 'Appointment Scheduled', text: 'नमस्ते {{patient_name}}, {{doctor_name}} के साथ आपकी अपॉइंटमेंट {{appointment_date}} को {{appointment_time}} बजे {{clinic_name}} में कन्फर्म हो गई है। किसी भी जानकारी के लिए {{mobile_number}} पर संपर्क करें।' },
                                        { label: 'Appointment Reminder', value: 'Appointment Reminder', text: 'रिमाइंडर: नमस्ते {{patient_name}}, हम आपको याद दिलाना चाहते हैं कि आपकी अपॉइंटमेंट {{doctor_name}} के साथ {{appointment_date}} को {{appointment_time}} बजे तय है। आपसे जल्द मिलेंगे!' },
                                        { label: 'Welcome to Clinic', value: 'Welcome to Clinic', text: '{{clinic_name}} में आपका स्वागत है, {{patient_name}}! हमें खुशी है कि आप यहाँ आए हैं। किसी भी सहायता के लिए कृपया {{mobile_number}} पर संपर्क करें।' },
                                        { label: 'Post-Consultation / Thank You', value: 'Thank You for Visiting', text: 'प्रिय {{patient_name}}, {{clinic_name}} पर आने के लिए धन्यवाद। हमें उम्मीद है कि {{doctor_name}} के साथ आपका अनुभव अच्छा रहा होगा। कृपया अपने स्वास्थ्य का ध्यान रखें!' },
                                        { label: 'Follow-up Reminder', value: 'Follow-up Reminder', text: 'नमस्ते {{patient_name}}, यह {{clinic_name}} से है। आपके आखिरी चेकअप को काफी समय हो गया है। कृपया हमें बताएं कि क्या आप {{doctor_name}} के साथ अगला चेकअप बुक करना चाहते हैं।' },
                                        { label: 'Reschedule Notice', value: 'Appointment Rescheduled', text: 'नमस्ते {{patient_name}}, {{clinic_name}} में आपकी अपॉइंटमेंट बदलकर {{appointment_date}} को {{appointment_time}} बजे {{doctor_name}} के साथ कर दी गई है। किसी भी असुविधा के लिए हमें खेद है।' },
                                        { label: 'Prescription Ready', value: 'Prescription Ready', text: 'नमस्ते {{patient_name}}, {{clinic_name}} में {{doctor_name}} द्वारा दी गई आपकी दवा पर्ची (Prescription) तैयार है। आप इसे अपनी अगली यात्रा के दौरान या हमारे पेशेंट पोर्टल के माध्यम से देख सकते हैं।' },
                                        { label: 'Lab Report Ready', value: 'Lab Report Ready', text: 'नमस्ते {{patient_name}}, आपकी लैब रिपोर्ट अब {{clinic_name}} में उपलब्ध है। अधिक जानकारी के लिए कृपया हमें {{mobile_number}} पर संपर्क करें।' },
                                        { label: 'Payment Confirmation', value: 'Payment Confirmation', text: 'प्रिय {{patient_name}}, हमें {{clinic_name}} में आपका भुगतान प्राप्त हो गया है। अपनी स्वास्थ्य देखभाल के लिए हमें चुनने के लिए धन्यवाद!' },
                                        { label: 'Feedback Request', value: 'Feedback Request', text: 'नमस्ते {{patient_name}}, {{clinic_name}} में आने के लिए धन्यवाद। हम {{doctor_name}} के साथ आपके अनुभव के बारे में जानना चाहेंगे। कृपया अपनी प्रतिक्रिया हमारे साथ साझा करें।' },
                                        { label: 'Birthday Wishes', value: 'Birthday Wishes', text: 'जन्मदिन मुबारक हो {{patient_name}}! हम आपके स्वस्थ और खुशहाल वर्ष की कामना करते हैं। सादर, {{clinic_name}}।' },
                                        { label: 'Medicine Reminder', value: 'Medicine Reminder', text: 'नमस्ते {{patient_name}}, यह {{clinic_name}} से आपकी निर्धारित दवा लेने के लिए एक रिमाइंडर है। स्वस्थ रहें!' },
                                        { label: 'Holiday Notice', value: 'Holiday Notice', text: 'प्रिय {{patient_name}}, कृपया ध्यान दें कि {{clinic_name}}, [Date] को [Holiday] के लिए बंद रहेगा। आपात स्थिति में, {{mobile_number}} पर संपर्क करें।' },
                                        { label: 'Treatment Scheduled', value: 'Treatment Scheduled', text: 'नमस्ते {{patient_name}}, [Treatment Name] के लिए आपका ट्रीटमेंट सेशन {{clinic_name}} में {{appointment_date}} को {{appointment_time}} बजे तय किया गया है। मिलते हैं!' }
                                    ],
                                    gujarati: [
                                        { label: 'Blank/Custom Template', value: '', text: '' },
                                        { label: 'Appointment Scheduled', value: 'Appointment Scheduled', text: 'નમસ્તે {{patient_name}}, {{doctor_name}} સાથે તમારી એપોઇન્ટમેન્ટ {{appointment_date}} ના રોજ {{appointment_time}} વાગ્યે {{clinic_name}} ખાતે કન્ફર્મ થઈ ગઈ છે. વધુ માહિતી માટે, {{mobile_number}} પર સંપર્ક કરો.' },
                                        { label: 'Appointment Reminder', value: 'Appointment Reminder', text: 'રિમાઇન્ડર: નમસ્તે {{patient_name}}, અમે તમને યાદ કરાવવા માંગીએ છીએ કે તમારી એપોઇન્ટમેન્ટ {{doctor_name}} સાથે {{appointment_date}} ના રોજ {{appointment_time}} વાગ્યે નક્કી છે. જલ્દી મળીશું!' },
                                        { label: 'Welcome to Clinic', value: 'Welcome to Clinic', text: '{{clinic_name}} માં તમારું સ્વાગત છે, {{patient_name}}! અમને ખુશી છે કે તમે અહી આવ્યા છો. કોઈપણ સહાય માટે કૃપા કરીને {{mobile_number}} પર અમારો સંપર્ક કરો.' },
                                        { label: 'Post-Consultation / Thank You', value: 'Thank You for Visiting', text: 'પ્રિય {{patient_name}}, {{clinic_name}} ની મુલાકાત લેવા બદલ આભાર. અમને આશા છે કે {{doctor_name}} સાથે તમારો અનુભવ સારો રહ્યો હશે. કૃપા કરીને તમારા સ્વાસ્થ્યની કાળજી લો!' },
                                        { label: 'Follow-up Reminder', value: 'Follow-up Reminder', text: 'નમસ્તે {{patient_name}}, આ {{clinic_name}} થી છે. તમને છેલ્લે તપાસ કર્યાને ઘણો સમય થયો છે. જો તમે {{doctor_name}} સાથે ફોલો-અપ શેડ્યૂલ કરવા માંગતા હોય તો અમને જણાવો.' },
                                        { label: 'Reschedule Notice', value: 'Appointment Rescheduled', text: 'નમસ્તે {{patient_name}}, {{clinic_name}} ખાતેની તમારી એપોઇન્ટમેન્ટ {{doctor_name}} સાથે {{appointment_date}} ના રોજ {{appointment_time}} વાગ્યે રિશેડ્યુઅલ કરવામાં આવી છે. અસુવિધા માટે ખેદ છે.' },
                                        { label: 'Prescription Ready', value: 'Prescription Ready', text: 'નમસ્તે {{patient_name}}, {{clinic_name}} ખાતે {{doctor_name}} દ્વારા આપેલ તમારું પ્રિસ્ક્રિપ્શન તૈયાર છે. તમે તમારી આગામી મુલાકાત દરમિયાન અથવા અમારા પેશન્ટ પોર્ટલ દ્વારા તેને જોઈ શકો છો.' },
                                        { label: 'Lab Report Ready', value: 'Lab Report Ready', text: 'નમસ્તે {{patient_name}}, તમારો લેબ રિપોર્ટ હવે {{clinic_name}} પર ઉપલબ્ધ છે. વધુ વિગતો માટે કૃપા કરીને {{mobile_number}} પર અમારો સંપર્ક કરો.' },
                                        { label: 'Payment Confirmation', value: 'Payment Confirmation', text: 'પ્રિય {{patient_name}}, અમને {{clinic_name}} ખાતે તમારી ચુકવણી મળી ગઈ છે. તમારી સ્વાસ્થ્ય સંભાળ માટે અમને પસંદ કરવા બદલ આભાર!' },
                                        { label: 'Feedback Request', value: 'Feedback Request', text: 'નમસ્તે {{patient_name}}, {{clinic_name}} ની મુલાકાત લેવા બદલ આભાર. અમને {{doctor_name}} સાથેના તમારા અનુભવ વિશે જાણવું ગમશે. કૃપા કરીને તમારો પ્રતિસાદ અમારી સાથે શેર કરો.' },
                                        { label: 'Birthday Wishes', value: 'Birthday Wishes', text: 'જન્મદિન મુબારક {{patient_name}}! તમારા આગામી વર્ષ માટે સારા સ્વાસ્થ્ય અને ખુશીઓની કામના. સાદર, {{clinic_name}}.' },
                                        { label: 'Medicine Reminder', value: 'Medicine Reminder', text: 'નમસ્તે {{patient_name}}, આ {{clinic_name}} તરફથી તમારી નિર્ધારિત દવા લેવા માટેનું રિમાઇન્ડર છે. સ્વસ્થ રહો!' },
                                        { label: 'Holiday Notice', value: 'Holiday Notice', text: 'પ્રિય {{patient_name}}, કૃપા કરીને નોંધો કે {{clinic_name}}, [Date] ના રોજ [Holiday] નિમિત્તે બંધ રહેશે. કટોકટીના કિસ્સામાં, {{mobile_number}} પર સંપર્ક કરો.' },
                                        { label: 'Treatment Scheduled', value: 'Treatment Scheduled', text: 'નમસ્તે {{patient_name}}, [Treatment Name] માટે તમારું ટ્રીટમેન્ટ સેશન {{clinic_name}} ખાતે {{appointment_date}} ના રોજ {{appointment_time}} વાગ્યે શેડ્યૂલ કરવામાં આવ્યું છે. ત્યાં મળીશું!' }
                                    ]
                                };

                                const currentLang = values.language || 'english';
                                const ACTIVE_TEMPLATES = DEFAULT_TEMPLATES_i18n[currentLang] || DEFAULT_TEMPLATES_i18n['english'];

                                return (
                                    <Form>
                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                                            {/* Left Column: Form & Variables */}
                                            <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                <Component4>
                                                    <Texts>
                                                        <LabelWithTooltip label="Start From Template" tooltip="Quickly load a predefined template for common scenarios." />
                                                    </Texts>
                                                    <InputSelect
                                                        name="default_template"
                                                        value={values.title && ACTIVE_TEMPLATES.some(t => t.value === values.title) ? values.title : ''}
                                                        options={ACTIVE_TEMPLATES}
                                                        onChange={(value) => {
                                                            const template = ACTIVE_TEMPLATES.find(t => t.value === value);
                                                            setFieldValue('title', template?.value || '');
                                                            setFieldValue('text', template?.text || '');
                                                        }}
                                                        variant="outlined"
                                                        placeholder="Select prebuilt template or create custom layout"
                                                        showNone={false}
                                                        sx={{
                                                            boxShadow: theme.shadows[1],
                                                            '& .MuiOutlinedInput-root': { height: '52px', borderRadius: '12px' },
                                                        }}
                                                    />
                                                </Component4>
                                                
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Component4 sx={{ flex: 1 }}>
                                                        <Texts>
                                                            <LabelWithTooltip label="Language" tooltip="Select the language for this message template." />
                                                        </Texts>
                                                        <InputSelect
                                                            name="language"
                                                            value={values.language}
                                                            options={Language}
                                                            onChange={(newLangValue) => {
                                                                setFieldValue('language', newLangValue);
                                                                // Auto-translate if they chose a default template
                                                                if (values.title && values.text) {
                                                                    const isTextADefault = Object.values(DEFAULT_TEMPLATES_i18n).some(langSet => 
                                                                        langSet.some(t => t.value === values.title && t.text === values.text)
                                                                    );
                                                                    if (isTextADefault) {
                                                                        const newLangTemplates = DEFAULT_TEMPLATES_i18n[newLangValue] || DEFAULT_TEMPLATES_i18n['english'];
                                                                        const translatedTemplate = newLangTemplates.find(t => t.value === values.title);
                                                                        if (translatedTemplate) {
                                                                            setFieldValue('text', translatedTemplate.text);
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                            variant="outlined"
                                                            placeholder="Select"
                                                            showNone={false}
                                                            sx={{
                                                                boxShadow: theme.shadows[1],
                                                                '& .MuiOutlinedInput-root': { height: '52px', borderRadius: '12px' },
                                                            }}
                                                        />
                                                    </Component4>
                                                    <Component4 sx={{ flex: 2 }}>
                                                        <Texts>
                                                            <LabelWithTooltip label="Message Template Name" tooltip="Enter a title for this message template." />
                                                        </Texts>
                                                        <InputTextComponent
                                                            name="title"
                                                            variant="outlined"
                                                            placeholder="e.g. Appointment Reminder..."
                                                            value={values.title}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                    </Component4>
                                                </Box>

                                                <Component4>
                                                    <Texts>
                                                        <LabelWithTooltip label="Message" tooltip="Enter the actual message content. Use custom variables to populate dynamic user data." />
                                                    </Texts>
                                                    <Box sx={{ 
                                                        border: `1px solid ${theme.palette.divider}`, 
                                                        borderRadius: '12px',
                                                        overflow: 'hidden'
                                                    }}>
                                                        {/* Variable Chips Header */}
                                                        <Box sx={{ 
                                                            backgroundColor: theme.palette.grey[50],
                                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                                            p: 1.5 
                                                        }}>
                                                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                                                Insert Variable
                                                            </Typography>
                                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                                {VARIABLE_GROUPS[0].variables.map((variable) => (
                                                                    <Chip
                                                                        key={variable.value}
                                                                        label={variable.label}
                                                                        size="small"
                                                                        onClick={() => {
                                                                            const textarea = textRef.current?.querySelector('textarea');
                                                                            if (textarea) {
                                                                                const start = textarea.selectionStart;
                                                                                const end = textarea.selectionEnd;
                                                                                const text = values.text;
                                                                                const before = text.substring(0, start);
                                                                                const after = text.substring(end, text.length);
                                                                                
                                                                                // Auto space logic
                                                                                const spaceBefore = (before && !before.endsWith(' ') && !before.endsWith('\n')) ? ' ' : '';
                                                                                const newValue = before + spaceBefore + variable.value + ' ' + after;
                                                                                setFieldValue('text', newValue);

                                                                                setTimeout(() => {
                                                                                    textarea.focus();
                                                                                    const cursorPosition = start + spaceBefore.length + variable.value.length + 1;
                                                                                    textarea.setSelectionRange(cursorPosition, cursorPosition);
                                                                                }, 0);
                                                                            } else {
                                                                                setFieldValue('text', values.text + (values.text && !values.text.endsWith(' ') ? ' ' : '') + variable.value + ' ');
                                                                            }
                                                                        }}
                                                                        sx={{
                                                                            borderRadius: '6px',
                                                                            backgroundColor: theme.palette.primary.light3,
                                                                            color: theme.palette.primary.main,
                                                                            cursor: 'pointer',
                                                                            fontWeight: 500,
                                                                            '&:hover': {
                                                                                backgroundColor: theme.palette.primary.light2,
                                                                            },
                                                                            mb: 0.5
                                                                        }}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                        
                                                        {/* Text Area */}
                                                        <InputTextComponent
                                                            ref={textRef}
                                                            name="text"
                                                            value={values.text}
                                                            variant="standard"
                                                            placeholder="Hello {{patient_name}}, your appointment is scheduled..."
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            fullWidth
                                                            multiline
                                                            rows={8}
                                                            InputProps={{ disableUnderline: true }}
                                                            sx={{
                                                                p: 2,
                                                                '& .MuiInputBase-root': {
                                                                    alignItems: 'flex-start',
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </Component4>

                                                <Box display="flex" justifyContent="flex-start" gap={2}>
                                                    <Button type="submit" variant="contained" color="primary">
                                                        Save Template
                                                    </Button>
                                                    <Button variant="outlined" onClick={() => {
                                                        setSelectedTemplate(null);
                                                        setOpenDialog(false);
                                                    }}>
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {/* Right Column: WhatsApp Preview Mockup */}
                                            <Box sx={{ 
                                                flex: 1, 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: theme.palette.grey[50], 
                                                borderRadius: '24px',
                                                p: 3,
                                                border: `1px dashed ${theme.palette.grey[300]}` 
                                            }}>
                                                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    WhatsApp Preview
                                                </Typography>
                                                
                                                {/* Phone Container */}
                                                <Box sx={{
                                                    width: '100%',
                                                    maxWidth: '300px',
                                                    height: '420px',
                                                    backgroundColor: '#E5DDD5', // WhatsApp background color
                                                    borderRadius: '24px',
                                                    position: 'relative',
                                                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                                    border: '8px solid #202020',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    overflow: 'hidden'
                                                }}>
                                                    {/* Header */}
                                                    <Box sx={{ 
                                                        height: '60px', 
                                                        backgroundColor: '#075E54', 
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        color: 'white'
                                                    }}>
                                                        <Box sx={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fff', mr: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#075E54', overflow: 'hidden' }}>
                                                            {previewData.clinic?.clinicName?.charAt(0) || 'C'}
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1 }}>
                                                                {previewData.clinic?.clinicName || 'Clinic Name'}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ fontSize: '10px', opacity: 0.8 }}>
                                                                Business Account
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Chat Area */}
                                                    <Box sx={{ 
                                                        flex: 1, 
                                                        p: 2, 
                                                        overflowY: 'auto',
                                                        backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
                                                        backgroundSize: 'cover'
                                                    }}>
                                                        {parsedPreviewText ? (
                                                            <Box sx={{ 
                                                                backgroundColor: '#DCF8C6', 
                                                                borderRadius: '8px', 
                                                                borderTopRightRadius: '0', 
                                                                p: 1.5,
                                                                position: 'relative',
                                                                boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
                                                            }}>
                                                                <Typography variant="body2" sx={{ 
                                                                    whiteSpace: 'pre-wrap', 
                                                                    color: '#303030',
                                                                    wordBreak: 'break-word',
                                                                    fontSize: '13px'
                                                                }}>
                                                                    {parsedPreviewText}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', fontSize: '10px', color: '#999', mt: 0.5 }}>
                                                                    {dayjs().format('HH:mm')} ✓✓
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ textAlign: 'center', mt: 5 }}>
                                                                <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.4)', backgroundColor: 'rgba(255,255,255,0.5)', px: 2, py: 0.5, borderRadius: '10px' }}>
                                                                    Type a message to preview...
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    {/* Input Mock */}
                                                    <Box sx={{ height: '50px', backgroundColor: '#F0F0F0', display: 'flex', alignItems: 'center', px: 1.5, gap: 1 }}>
                                                        <Box sx={{ flex: 1, height: '36px', backgroundColor: '#fff', borderRadius: '18px' }} />
                                                        <Box sx={{ width: '36px', height: '36px', backgroundColor: '#00897B', borderRadius: '50%' }} />
                                                    </Box>
                                                </Box>

                                            </Box>
                                        </Box>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default React.memo(MessageTemplate);
